'use client';

import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import type {
  AdvancedMarkerItem,
  CreateGoogleMapOptions,
  GoogleMapsLibraries,
  LatLng,
} from './types';

let configuredApiKey: string | null = null;
let librariesPromise: Promise<GoogleMapsLibraries> | null = null;

/**
 * Loads the Google Maps JavaScript API and required libraries.
 * Caches the loaded libraries to prevent redundant loads.
 *
 * @param apiKey - Your Google Maps API key.
 * @returns A promise that resolves to the loaded Google Maps libraries.
 */
async function loadGoogleMaps(apiKey: string): Promise<GoogleMapsLibraries> {
  if (configuredApiKey && configuredApiKey !== apiKey) {
    throw new Error('Google Maps loader was already initialized with a different API key.');
  }

  if (!configuredApiKey) {
    setOptions({
      key: apiKey,
      v: 'weekly',
    });

    configuredApiKey = apiKey;
  }

  if (!librariesPromise) {
    librariesPromise = Promise.all([
      importLibrary('maps') as Promise<google.maps.MapsLibrary>,
      importLibrary('marker') as Promise<google.maps.MarkerLibrary>,
    ]).then(([mapsLib, markerLib]) => ({
      Map: mapsLib.Map,
      AdvancedMarkerElement: markerLib.AdvancedMarkerElement,
      PinElement: markerLib.PinElement,
    }));
  }

  return librariesPromise;
}

/**
 * Creates a Google Map instance in the specified container.
 *
 * @param options - Configuration options for the map.
 * @returns A promise that resolves to the created Google Map instance.
 */
export async function createGoogleMap({
  apiKey,
  container,
  mapId,
  center = { lat: 39.8283, lng: -98.5795 },
  zoom = 5,
  disableDefaultUI = false,
  gestureHandling = 'cooperative',
  mapTypeControl = false,
  streetViewControl = false,
  fullscreenControl = false,
  panControl = false,
  rotateControl = false,
  zoomControl = true,
  cameraControl = false,
}: CreateGoogleMapOptions): Promise<google.maps.Map> {
  const { Map } = await loadGoogleMaps(apiKey);

  return new Map(container, {
    center,
    zoom,
    mapId,
    disableDefaultUI,
    gestureHandling,
    mapTypeControl,
    streetViewControl,
    fullscreenControl,
    panControl,
    rotateControl,
    zoomControl,
    cameraControl,
  });
}

/**

 * Adds advanced markers to the specified Google Map instance.
 *
 * @param apiKey - Your Google Maps API key.
 * @param map - The Google Map instance to add markers to.
 * @param items - An array of marker items to add to the map.
 * @returns A promise that resolves to an array of created AdvancedMarkerElement instances.
 */
export async function addAdvancedMarkers(
  apiKey: string,
  map: google.maps.Map,
  items: AdvancedMarkerItem[],
): Promise<google.maps.marker.AdvancedMarkerElement[]> {
  const { AdvancedMarkerElement } = await loadGoogleMaps(apiKey);

  return items.map((item) => {
    const marker = new AdvancedMarkerElement({
      map,
      position: item.position,
      title: item.title,
      content: item.content,
      zIndex: item.zIndex,
    });

    if (item.onClick) {
      marker.addListener('click', (event: google.maps.MapMouseEvent) => {
        item.onClick?.(marker, event);
      });
    }

    return marker;
  });
}

/**
 * Adjusts the map view to fit the specified points with optional padding.
 *
 * @param map - The Google Map instance to adjust.
 * @param points - An array of LatLng points to fit within the map view.
 * @param padding - Optional padding around the bounds (default is 64 pixels).
 */
export function fitMapToPoints(
  map: google.maps.Map,
  points: LatLng[],
  padding: number | google.maps.Padding = 64,
) {
  if (!points.length) return;

  const bounds = new google.maps.LatLngBounds();

  points.forEach((point) => bounds.extend(point));
  map.fitBounds(bounds, padding);
}

/**
 * Creates a default pin element using the Google Maps Marker library.
 *
 * @param apiKey - Your Google Maps API key.
 * @param options - Optional configuration for the pin element.
 * @returns A promise that resolves to the created pin element as an HTMLElement.
 */
export async function createDefaultPin(
  apiKey: string,
  options?: ConstructorParameters<typeof google.maps.marker.PinElement>[0],
): Promise<HTMLElement> {
  const { PinElement } = await loadGoogleMaps(apiKey);
  const pin = new PinElement(options);

  return pin.element;
}
